/**
 * Due diligence projects: their own store, as valuations have theirs.
 *
 * A diligence project pins a company's statements from an accounting model once and runs the
 * checks on them on the server; this store holds the open project, its assessment and the
 * list. It reads no other store - the accounting models it can link to are listed by their
 * own endpoint - so the discipline stays separate. See docs/due-diligence-plan.md.
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

import { useAuthStore } from './auth';

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function jsonHeaders(): Record<string, string> {
  return { 'Content-Type': 'application/json', ...authHeaders() };
}

const BASE = '/api/v2/diligence';

export type Severity = 'high' | 'medium' | 'low' | 'info';
export type Status = 'open' | 'proposed' | 'accepted' | 'rejected';

export interface Finding {
  id: string;
  tab: string;
  title: string;
  amount: number | null;
  year: number | null;
  severity: Severity;
  source: string;
  computation: string;
  status: Status;
  unvouched: boolean;
  /** The figures it rests on and where each is printed (the data room's procedures). */
  citations?: Citation[];
}

export interface Citation {
  document: string;
  page: number | null;
  row: string | null;
  period: string | null;
  figure: number;
  label: string;
}

export interface Adjustment {
  id: string;
  label: string;
  year: number;
  amount: number;
  category: string;
  source: string;
  status: Status;
  proposedBy: 'user' | 'model';
  quote: string;
}

export interface NetDebtItem {
  key: string;
  label: string;
  kind: string;
  amount: number;
  included: boolean;
  reason: string;
  source: string;
  switched: boolean;
}

export interface TabResult {
  findings: Finding[];
  notAssessed?: string[];
}

export interface Assessment {
  overview: { findings: Finding[]; notAssessed: string[]; unvouched: string[]; examined: string[] };
  'net-debt': TabResult & { year: number | null; items: NetDebtItem[]; total: number | null };
  'working-capital': TabResult & {
    rows: Array<{ year: number; debtorDays: number | null; creditorDays: number | null; stockDays: number | null; workingCapital: number }>;
    normalised: number | null;
    normalisedYears: number;
  };
  'cash-conversion': TabResult & {
    rows: Array<{
      year: number; operatingCashFlow: number; ebitda: number; profitAfterTax: number;
      conversion: number | null; profitCover: number | null; capex: number; depreciation: number;
      capexToDepreciation: number | null;
    }>;
  };
  'quality-of-earnings': TabResult & {
    rows: Array<{ year: number; reported: number; adjustments: Adjustment[]; accepted: number; adjusted: number; pending: number }>;
  };
  corporate: TabResult & { register: CompanyRegister | null };
  documents: TabResult & { signals: FilingSignal[]; series: MonthlySeries | null };
  'related-parties': TabResult & { note: RelatedPartyNote | null; validation: string };
  'proof-of-cash': TabResult & {
    accounts: Array<{ account: string; bank: string; statements: string[]; start: string | null; end: string | null; proved: boolean }>;
    months: Array<{ month: string; closing: number; receipts: number; payments: number }>;
    validation: string;
  };
  triangulation: TabResult & {
    quarters: Array<{ start: string; end: string; vatSales: number; vatOnSales: number; ledger: number | null; receipts: number | null }>;
    years: Array<Record<string, number | null>>;
    validation: string;
  };
  'red-flags': TabResult & {
    screens: Array<{ key: string; label: string; value: number; detail: string; flagged: boolean; year: number }>;
  };
}

export interface RequestItem {
  id: string;
  text: string;
  source: string;
  status: 'open' | 'sent' | 'closed';
}

export interface CompanyRegister {
  number: string;
  name: string;
  status: string;
  incorporated: string;
  sicCodes: string[];
  accountsOverdue: boolean;
  accountsNextDue: string;
  accountsLastMadeUpTo: string;
  confirmationOverdue: boolean;
  hasInsolvencyHistory: boolean;
  officers: Array<{ name: string; role: string; appointedOn: string; resignedOn: string }>;
  control: Array<{ name: string; kind: string; naturesOfControl: string[]; notifiedOn: string; ceasedOn: string }>;
  charges: Array<{ status: string; createdOn: string; satisfiedOn: string; holders: string[]; classification: string }>;
  filings: Array<{ date: string; category: string; type: string; description: string }>;
  retrievedAt: string;
}

export interface FilingSignal {
  kind: string;
  severity: Severity;
  title: string;
  page: number | null;
  quote: string;
}

export interface Clause {
  id: string;
  document: string;
  kind: string;
  page: number;
  quote: string;
  summary: string;
  status: 'proposed' | 'accepted' | 'rejected';
}

export interface ManagementAccounts {
  name: string;
  uploadedAt: string;
  rows: Array<{ label: string; field: string | null; values: Record<string, number> }>;
}

export interface MonthlyPack {
  name: string;
  uploadedAt: string;
  rows: Array<{ label: string; field: string | null; months: Record<string, number> }>;
}

export interface SeriesYearLine {
  field: string;
  months?: number;
  management?: number;
  filed?: number;
  difference?: number;
  differs?: boolean;
  compared: boolean;
}

/** The monthly packs as one series, reconciled to the filed years (app/diligence/series.py). */
export interface MonthlySeries {
  months: string[];
  fields: Record<string, Record<string, number>>;
  yearEndMonth: number | null;
  yearEndSource: string;
  years: Array<{ year: number; months: number; filed: boolean; lines: SeriesYearLine[] }>;
  ltm: Record<string, Record<string, number>>;
  grossMargin: Record<string, number>;
  seasonality: Record<string, number>;
  runRate: { months: string[]; annualised: number; filedYear: number; filed: number; change: number | null } | null;
  unmatched: string[];
  validation: string;
  notAssessed: string[];
}

/** The related-party note as code read it (app/services/v2_related_parties.py). */
export interface RelatedPartyNote {
  found: boolean;
  page: number | null;
  none: boolean;
  noneStatement: string;
  rows: Array<{
    counterparty: string;
    relationship: string;
    nature: string;
    year: number | null;
    amount: number;
    prior: number | null;
    page: number | null;
    quote: string;
    shape: string;
    proved: boolean;
    implied: boolean;
  }>;
  unread: string[];
}

export interface ContractRead {
  name: string;
  uploadedAt: string;
  pages: number;
  clausesFound: number;
  clausesDropped: number;
}

export interface DiligenceVersion {
  version: number;
  savedAt: string;
  note: string;
}

export interface DiligenceProject {
  id: string;
  name: string;
  description: string;
  source: { kind: 'linked' | 'none' | 'example'; modelId: string | null; modelName: string | null; pinnedAt: string | null; companyNumber: string | null };
  statements: { historicalYears: number[]; currency: string; hasIncomeStatement: boolean; unvouched: string[] };
  config: {
    netDebtOverrides: Record<string, boolean>;
    netDebtReasons: Record<string, string>;
    normalisationYears: number;
    yearEndMonth: number | null;
  };
  adjustments: Adjustment[];
  decisions: Record<string, Status>;
  versions: DiligenceVersion[];
  companyRegister: CompanyRegister | null;
  requests: RequestItem[];
  sentTo: string[];
  managementAccounts: ManagementAccounts | null;
  monthlyPacks: MonthlyPack[];
  bankStatements: Array<{ name: string; account: string; bank: string; start: string | null; end: string | null; proved: boolean; notes: string[] }>;
  vatReturns: Array<{ name: string; start: string | null; end: string | null; proved: boolean; fault: string }>;
  payeSummaries: Array<{ name: string; months: unknown[]; notes: string[] }>;
  narrative: { writtenAt: string; paragraphs: Array<{ text: string; findings: string[] }>; dropped: string[] } | null;
  contracts: ContractRead[];
  clauses: Clause[];
  version: number;
  assessment: Assessment;
  updatedAt: string;
}

export interface DiligenceRow {
  id: string;
  name: string;
  sourceKind: string;
  modelName: string | null;
  pinnedAt: string | null;
  years: number[];
  findings: number;
  high: number;
  updatedAt: string;
}

export interface DiligenceExample {
  key: string;
  name: string;
  summary: string;
  teaches: string[];
}

export interface LinkableModel {
  id: string;
  name: string;
}

interface DiligenceState {
  list: DiligenceRow[];
  models: LinkableModel[];
  valuations: LinkableModel[];
  examples: DiligenceExample[];
  project: DiligenceProject | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  dropped: string[];
  listProjects: () => Promise<void>;
  listModels: () => Promise<void>;
  listExamples: () => Promise<void>;
  openExample: (key: string) => Promise<void>;
  create: (name: string, modelId: string | null) => Promise<void>;
  open: (id: string) => Promise<void>;
  close: () => void;
  remove: (id: string) => Promise<void>;
  updateConfig: (config: Record<string, unknown>, note?: string) => Promise<void>;
  decide: (findingId: string, status: Status) => Promise<void>;
  addAdjustment: (adjustment: { label: string; year: number; amount: number; category: string }) => Promise<void>;
  decideAdjustment: (adjustmentId: string, status: Status) => Promise<void>;
  proposeAdjustments: () => Promise<void>;
  refresh: () => Promise<void>;
  restore: (version: number) => Promise<void>;
  exportReport: (fmt: 'xlsx' | 'docx') => Promise<void>;
  readRegister: (companyNumber: string) => Promise<void>;
  generateRequests: () => Promise<void>;
  addRequest: (text: string) => Promise<void>;
  editRequest: (requestId: string, change: { text?: string; status?: string }) => Promise<void>;
  listValuations: () => Promise<void>;
  sendToValuation: (valuationId: string) => Promise<void>;
  uploadManagementAccounts: (file: File, yearEndMonth?: number | null) => Promise<void>;
  removeManagementAccounts: () => Promise<void>;
  removeMonthlyPack: (name: string) => Promise<void>;
  uploadDocument: (kind: 'bank-statements' | 'vat-returns' | 'payroll', file: File, account?: string) => Promise<void>;
  removeDocument: (kind: 'bank-statements' | 'vat-returns' | 'payroll', name: string) => Promise<void>;
  writeNarrative: () => Promise<void>;
  uploadContract: (file: File) => Promise<void>;
  decideClause: (clauseId: string, status: 'accepted' | 'rejected' | 'proposed') => Promise<void>;
  clearError: () => void;
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, init);
  if (!response.ok) {
    let detail = `${response.status}`;
    try {
      detail = (await response.json()).detail ?? detail;
    } catch {
      // not JSON
    }
    throw new Error(String(detail));
  }
  return (await response.json()) as T;
}

export const useDiligenceStore = create<DiligenceState>()(
  immer((set, get) => {
    // Every action that changes the project returns it whole, assessment included.
    const mutate = async (url: string, init: RequestInit) => {
      set((s) => {
        s.isSaving = true;
        s.error = null;
      });
      try {
        const project = await request<DiligenceProject & { dropped?: string[] }>(url, init);
        set((s) => {
          s.project = project;
          s.dropped = project.dropped ?? [];
          s.isSaving = false;
        });
      } catch (error) {
        set((s) => {
          s.isSaving = false;
          s.error = error instanceof Error ? error.message : String(error);
        });
      }
    };
    const id = () => get().project?.id ?? '';
    return {
      list: [],
      models: [],
      valuations: [],
      examples: [],
      project: null,
      isLoading: false,
      isSaving: false,
      error: null,
      dropped: [],
      listProjects: async () => {
        set((s) => {
          s.isLoading = true;
        });
        try {
          const list = await request<DiligenceRow[]>(BASE, { headers: authHeaders() });
          set((s) => {
            s.list = list;
            s.isLoading = false;
          });
        } catch (error) {
          set((s) => {
            s.isLoading = false;
            s.error = error instanceof Error ? error.message : String(error);
          });
        }
      },
      listModels: async () => {
        try {
          const models = await request<LinkableModel[]>('/api/v2/models', { headers: authHeaders() });
          set((s) => {
            s.models = models.map((m) => ({ id: m.id, name: m.name }));
          });
        } catch {
          // No models to link: a corporate-only project can still be started.
        }
      },
      listExamples: async () => {
        try {
          const examples = await request<DiligenceExample[]>(`${BASE}/examples`, { headers: authHeaders() });
          set((s) => {
            s.examples = examples;
          });
        } catch {
          // No examples is no loss: the page still starts real projects.
        }
      },
      openExample: async (key) => {
        // A new project of the user's own, opened straight away.
        await mutate(`${BASE}/examples/${encodeURIComponent(key)}`, { method: 'POST', headers: authHeaders() });
        await get().listProjects();
      },
      create: async (name, modelId) => {
        await mutate(BASE, {
          method: 'POST',
          headers: jsonHeaders(),
          body: JSON.stringify({ name, source: modelId ? { kind: 'linked', modelId } : { kind: 'none' } }),
        });
        await get().listProjects();
      },
      open: async (projectId) => {
        await mutate(`${BASE}/${projectId}`, { headers: authHeaders() });
      },
      close: () =>
        set((s) => {
          s.project = null;
          s.dropped = [];
        }),
      remove: async (projectId) => {
        await request(`${BASE}/${projectId}`, { method: 'DELETE', headers: authHeaders() }).catch(() => undefined);
        await get().listProjects();
      },
      updateConfig: async (config, note = '') => {
        await mutate(`${BASE}/${id()}`, { method: 'PATCH', headers: jsonHeaders(), body: JSON.stringify({ config, note }) });
      },
      decide: async (findingId, status) => {
        await mutate(`${BASE}/${id()}/findings/${encodeURIComponent(findingId)}`, {
          method: 'POST', headers: jsonHeaders(), body: JSON.stringify({ status }),
        });
      },
      addAdjustment: async (adjustment) => {
        await mutate(`${BASE}/${id()}/adjustments`, { method: 'POST', headers: jsonHeaders(), body: JSON.stringify(adjustment) });
      },
      decideAdjustment: async (adjustmentId, status) => {
        await mutate(`${BASE}/${id()}/adjustments/${encodeURIComponent(adjustmentId)}`, {
          method: 'POST', headers: jsonHeaders(), body: JSON.stringify({ status }),
        });
      },
      proposeAdjustments: async () => {
        await mutate(`${BASE}/${id()}/propose-adjustments`, { method: 'POST', headers: authHeaders() });
      },
      refresh: async () => {
        await mutate(`${BASE}/${id()}/refresh`, { method: 'POST', headers: authHeaders() });
      },
      restore: async (version) => {
        await mutate(`${BASE}/${id()}/versions/${version}/restore`, { method: 'POST', headers: authHeaders() });
      },
      exportReport: async (fmt) => {
        const project = get().project;
        if (!project) return;
        const response = await fetch(`${BASE}/${project.id}/export?fmt=${fmt}`, { headers: authHeaders() });
        if (!response.ok) {
          set((s) => {
            s.error = `Export failed (${response.status})`;
          });
          return;
        }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${project.name}.${fmt}`;
        link.click();
        URL.revokeObjectURL(url);
      },
      readRegister: async (companyNumber) => {
        await mutate(`${BASE}/${id()}/register`, { method: 'POST', headers: jsonHeaders(), body: JSON.stringify({ companyNumber }) });
      },
      generateRequests: async () => {
        await mutate(`${BASE}/${id()}/requests/generate`, { method: 'POST', headers: authHeaders() });
      },
      addRequest: async (text) => {
        await mutate(`${BASE}/${id()}/requests`, { method: 'POST', headers: jsonHeaders(), body: JSON.stringify({ text }) });
      },
      editRequest: async (requestId, change) => {
        await mutate(`${BASE}/${id()}/requests/${encodeURIComponent(requestId)}`, {
          method: 'POST', headers: jsonHeaders(), body: JSON.stringify(change),
        });
      },
      listValuations: async () => {
        try {
          const rows = await request<LinkableModel[]>('/api/v2/valuations', { headers: authHeaders() });
          set((s) => {
            s.valuations = rows.map((v) => ({ id: v.id, name: v.name }));
          });
        } catch {
          // None to send to.
        }
      },
      sendToValuation: async (valuationId) => {
        await mutate(`${BASE}/${id()}/send-to-valuation`, { method: 'POST', headers: jsonHeaders(), body: JSON.stringify({ valuationId }) });
      },
      uploadManagementAccounts: async (file, yearEndMonth) => {
        const body = new FormData();
        body.append('file', file);
        if (yearEndMonth) body.append('yearEndMonth', String(yearEndMonth));
        await mutate(`${BASE}/${id()}/management-accounts`, { method: 'POST', headers: authHeaders(), body });
      },
      removeManagementAccounts: async () => {
        await mutate(`${BASE}/${id()}/management-accounts`, { method: 'DELETE', headers: authHeaders() });
      },
      uploadDocument: async (kind, file, account) => {
        const body = new FormData();
        body.append('file', file);
        if (account) body.append('account', account);
        await mutate(`${BASE}/${id()}/${kind}`, { method: 'POST', headers: authHeaders(), body });
      },
      removeDocument: async (kind, name) => {
        await mutate(`${BASE}/${id()}/${kind}/${encodeURIComponent(name)}`, { method: 'DELETE', headers: authHeaders() });
      },
      writeNarrative: async () => {
        await mutate(`${BASE}/${id()}/narrative`, { method: 'POST', headers: authHeaders() });
      },
      removeMonthlyPack: async (name) => {
        await mutate(`${BASE}/${id()}/monthly-packs/${encodeURIComponent(name)}`, { method: 'DELETE', headers: authHeaders() });
      },
      uploadContract: async (file) => {
        const body = new FormData();
        body.append('file', file);
        await mutate(`${BASE}/${id()}/contracts`, { method: 'POST', headers: authHeaders(), body });
      },
      decideClause: async (clauseId, status) => {
        await mutate(`${BASE}/${id()}/clauses/${encodeURIComponent(clauseId)}`, {
          method: 'POST', headers: jsonHeaders(), body: JSON.stringify({ status }),
        });
      },
      clearError: () =>
        set((s) => {
          s.error = null;
        }),
    };
  }),
);
